import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';
import qs from 'qs';
import ROLES from 'constants/UserRoles';
import LandingModule from 'components/modules/landing/LandingModule';
import Booking from 'components/modules/booking/v2/Booking';
import Login from 'components/session/login';
import CustomerDetails from 'components/modules/user/customer-details';
import AccountAccessVerification from 'components/modules/user/account-access-verification';
import ShipmentDetailPage from 'components/modules/shipments/ShipmentDetailsFullPage';
import ReferenceNumbersMasterList from 'components/modules/tools/ReferenceNumbersMasterList';
import DoRequestChanges from 'components/modules/shipments/delivery-order/RequestChanges';
import ShipmentExceptions from 'components/modules/exceptions/ShipmentExceptions';
import OrderExceptions from 'components/modules/exceptions/OrderExceptions';
import AccessorialExceptions from 'components/modules/exceptions/AccessorialExceptions';
import InvoiceModule from 'components/modules/accounting/invoices/invoice-module';
import InvoiceDetails from 'components/modules/accounting/invoices/invoice-details';
import PayoutModule from 'components/modules/accounting/payouts/payout-module';
import PayoutDetails from 'components/modules/accounting/payouts/payout-details';
import ContractList from 'components/modules/accounting/contracts/ContractList';
import ContractsEditorModal from 'components/modules/accounting/contracts/ContractsEditorModal';
import SettingsModule from 'components/modules/settings/settings-module';
import GrowthReport from 'components/modules/reporting/GrowthReport';
import DailyReport from 'components/modules/reporting/DailyReport';
import TariffsTable from 'components/modules/tools/TariffsTable';
import FleetView from 'components/modules/fleet/FleetView';
import PasswordReset from 'components/modules/selfcare/PasswordReset';
import ShipperBillingForm from 'components/modules/user/shipper/billing/MainForm';
import ShipmentListModule from 'components/modules/shipments/list/ShipmentListModule';
import PublicShipmentTracking from 'components/modules/public/tracking';
import SessionStore from 'stores/SessionStore';
import SupportRequestVerification from 'components/site/support-request-verification';
import LaneRateResponse from 'components/site/lane-rate-response';
import LaneRatesRC from 'components/modules/pricing/rate-conditions/lane-rates/LaneRatesRateConditions';
import AccessorialRatesRateConditions from 'components/modules/pricing/rate-conditions/accessorial-rates/AccessorialRatesRateConditions';
import AccessorialRateResponse from 'components/site/accessorial-rate-response';
import ToolsPage from 'components/modules/ToolsPage';
import { AuthorizationWrapper } from 'components/wrappers/AuthorizationWrapper';
import { AuthContext } from 'providers/authorization-context';
import { isProd } from 'helpers/helper_functions';
import AccessorialRequestsPage from 'components/modules/user/shipper/AccessorialRequestsPage';
import StopDocumentRequirementsPage from 'components/modules/user/shipper/StopDocumentRequirementsPage';
import ReferenceNumbersPage from 'components/modules/user/shipper/ReferenceNumbersPage';
import USER_TYPES from 'constants/UserTypes';
import WarehouseUserProfile from 'components/modules/user/warehouse/Profile';
import EmptyNotify from 'components/modules/user/warehouse/EmptyNotify';
import CacheBusting from './CacheBusting';
import CarrierDocuments from './modules/carrier/Documents';
import CarrierDashboard from './modules/carrier/Dashboard';
import BulkAuditTool from './modules/carrier/BulkAuditTool';
import CarrierBilling from './modules/carrier/Billing';
import NotificationsPage from './modules/carrier/NotificationPage';
import UserNotificationsPage from './modules/user/shipper/NotificationPage';
import CarrierPayoutModule from './modules/accounting/payouts/carrier/payout-module';
import CarrierPayoutDetails from './modules/accounting/payouts/carrier/payout-details';
import DriverMap from './modules/drivers/DriverMap';
import DriverList from './modules/drivers/DriverList';
import DriverEditForm from './modules/drivers/DriverEditForm';
import ShipmentTracking from './modules/shipments/ShipmentTracking';
import Equipment from './modules/equipment/List';
import LocationIndex from './modules/locations';
import LocationModule from './modules/locations/Module';
import QuotesList from './modules/quotes/List';
import DrayageQuotePage from './modules/quotes/DrayageQuotePage';
import MarketsIndex from './modules/markets';
import CustomerList from './modules/customers/Customers';
import OrderDetails from './modules/orders/Module';
import OrderList from './modules/orders/List';
import ExploreUnifiedView from './modules/master/UnifiedViewFlagWrapper';
import ExploreOrdersUnifiedView from './modules/master/orders/Explore';
import { Dashboard as UnifiedViewDashboard } from './modules/master/Dashboard';
import Reports from './modules/master/Reports';
import UVColumnConfig from './modules/master/UVColumnConfig';
import AccessorialModule from './modules/shipper/AccessorialModule';
import Zones from './modules/tools/Zones';
import MetricsPage from './modules/metrics/Page';
import AccessorialManagementCenter from './modules/tools/AccessorialManagementCenter';
import MarketplaceConfig from './modules/marketplace-config/Markup';
import ReasonCodes from './modules/marketplace-config/ReasonCodes';
import LoadBoard from './offers/LoadBoard';
import DeveloperDetails from './modules/user/shipper/developer-details';
import AccessorialRates from './modules/pricing/AccessorialRates';
import AppointmentModule from './modules/appointments/AppointmentModule';
import AppointmentDetails from './modules/appointments/AppointmentDetails';
import BulkShipmentCreation from './modules/shipments/BulkShipmentUpload';
import OfferPlanner from './modules/offer-planner/OfferPlanner';
import EmptyReturnInstructions from './modules/public/emptyInstructions';
import YardManagement from './modules/user/YardManagement';
import IntegrationSettings from './modules/user/shipper/IntegrationSettings';
import CarrierIntegrationSettings from './modules/user/carrier/CarrierIntegrationSettings';
import BillingV2 from './modules/user/shipper/billingv2';
import Team from './modules/user/shipper/team';
import AccountActivity from './modules/user/AccountActivity';
import YardsList from './modules/yards/YardsList';
import YardDetails from './modules/yards/YardDetails';
import MarketRates from './modules/yards/MarketRates';
import DriversSettings from './modules/user/carrier/DriversSettings';
import NotificationsSettings from './modules/user/carrier/NotificationsSettings';
import ActivitySettings from './modules/user/carrier/ActivitySettings';
import BillingSettings from './modules/user/carrier/BillingSettings';
import QuickPay from './modules/user/carrier/quickpay/QuickPay';
import DrayageFscSettings from './modules/user/rates/drayage/FscSettings';
import ShorthaulFscSettings from './modules/user/rates/shorthaul/FscSettings';
import LaneRatesRateCondtionsEditPage from './modules/pricing/rate-conditions/lane-rates/LaneRatesEditPage';
import EmptyNotifyRequests from './modules/user/warehouse/EmptyNotifyRequests';
import ManageWarehouse from './modules/warehouse/ManageWarehouse';
import ErrorBoundary from './ErrorBoundary';
import TestErrorPage from './TestErrorPage';
import ManageQuickPayDiscountTerms from './modules/quickpayments/ManageQuickPayDiscountTerms';
import { PageProvider } from './modules/master/PageContext';
import CarrierTeam from './modules/user/carrier/team';
import RateCard from './v2/RateCard';
function routerErrorHandler(error) {
    console.error('Error occurred while routing');
    console.error(error.message);
    console.error(error.stack);
}
function getCarrierDashboardComponent() {
    if (SessionStore.isShipper()) {
        return null;
    }
    return CarrierDashboard;
}
function getBillingComponent() {
    if (SessionStore.isShipper()) {
        return ShipperBillingForm;
    }
    if (SessionStore.isCarrier()) {
        return CarrierBilling;
    }
    return null;
}
function getAccessorialRequestsPage() {
    if (SessionStore.isAdmin()) {
        return React.createElement(AccessorialRequestsPage, null);
    }
    return React.createElement(Redirect, { to: "/" });
}
function getStopDocumentRequirementsPage() {
    if (SessionStore.isAdmin()) {
        return React.createElement(StopDocumentRequirementsPage, null);
    }
    return React.createElement(Redirect, { to: "/" });
}
function getReferenceNumberPage() {
    if (SessionStore.isAdmin()) {
        return React.createElement(ReferenceNumbersPage, null);
    }
    return React.createElement(Redirect, { to: "/" });
}
function getShipmentDetailComponent() {
    if (SessionStore.isAdmin()) {
        return ShipmentDetailPage;
    }
    return ShipmentTracking;
}
function getQueryString(path = '') {
    const query = path.split('?');
    if (!query) {
        return null;
    }
    return qs.parse(query[query.length - 1], { ignoreQueryPrefix: true });
}
function checkRedirectPath(query = {}, path = '') {
    return !query.action || !/request-changes/.test(path);
}
const RedirectToHome = () => {
    return React.createElement(Redirect, { to: "/" });
};
function getLandingPage() {
    const user = SessionStore.getUser();
    if (user._type === 'Carrier' && !SessionStore.isThirdPartyCarrier(user)) {
        return getCarrierDashboardComponent();
    }
    return LandingModule;
}
// function getLaneRates() {
//   if (SessionStore.isAdmin()) {
//     return <LaneRates />;
//   }
//   return <Redirect to="/" />;
// }
// function getLaneRatesEdit() {
//   if (SessionStore.isAdmin()) {
//     return <LaneRatesEditPage />;
//   }
//   return <Redirect to="/" />;
// }
function getLaneRatesRateConditionsEdit() {
    if (SessionStore.isAdmin()) {
        return React.createElement(LaneRatesRateCondtionsEditPage, null);
    }
    return React.createElement(Redirect, { to: "/" });
}
function getAccessorialRates() {
    if (SessionStore.isAdmin()) {
        return React.createElement(AccessorialRates, null);
    }
    return React.createElement(Redirect, { to: "/" });
}
function getOfferPlanner() {
    if (SessionStore.isAdmin()) {
        return React.createElement(OfferPlanner, null);
    }
    return React.createElement(Redirect, { to: "/" });
}
function renderWarehouseRoutes(redirect) {
    const status = SessionStore.getShipperWarehouseRelationStatus();
    const isShipperRelationEnabled = status === 'true';
    if (redirect) {
        return React.createElement(Redirect, { to: redirect });
    }
    return (React.createElement(Switch, null,
        React.createElement(Route, { exact: true, path: "/", component: EmptyNotify }),
        React.createElement(Route, { exact: true, path: "/warehouse/user/profile", component: WarehouseUserProfile }),
        isShipperRelationEnabled && (React.createElement(Route, { exact: true, path: "/warehouse/empty-notify-requests/:reportId", component: EmptyNotifyRequests })),
        React.createElement(Route, { path: "*", component: RedirectToHome })));
}
function renderPrivate(redirect) {
    return (React.createElement("div", { className: "" },
        redirect && React.createElement(Redirect, { to: redirect }),
        React.createElement(Route, { exact: true, path: "/", component: getLandingPage() }),
        React.createElement(Route, { exact: true, name: "test-error-page", path: "/test-error", component: TestErrorPage }),
        !SessionStore.isMessageOnlyUser() && (React.createElement("div", null,
            React.createElement("div", { className: "" },
                React.createElement(Route, { exact: true, name: "fleet", path: "/fleet", component: FleetView }),
                React.createElement(Route, { exact: true, name: "quotes", path: "/quotes/:id?", component: QuotesList }),
                React.createElement(Route, { exact: true, name: "drayage-quotes", path: "/drayage-quotes", component: DrayageQuotePage }),
                React.createElement(Route, { exact: true, name: "booking", path: "/booking/:id?", component: Booking }),
                React.createElement(Route, { exact: true, name: "landing", path: "/landing", component: LandingModule }),
                React.createElement(Route, { exact: true, name: "shipment", path: "/shipments/:id", component: getShipmentDetailComponent() }),
                React.createElement(Route, { exact: true, name: "shipment-request-changes", path: "/shipments/:id/request-changes", component: DoRequestChanges }),
                React.createElement(Route, { exact: true, name: "shipments", path: "/shipments", component: ShipmentListModule }),
                React.createElement(Route, { exact: true, name: "exceptions/shipments", path: "/exceptions/shipments", component: ShipmentExceptions }),
                React.createElement(Route, { exact: true, name: "exceptions/orders", path: "/exceptions/orders", component: OrderExceptions }),
                React.createElement(Route, { exact: true, name: "exceptions/accessorials", path: "/exceptions/accessorials", component: AccessorialExceptions }),
                React.createElement(Route, { exact: true, name: "invoices", path: "/invoices", component: InvoiceModule }),
                React.createElement(Route, { exact: true, name: "accessorials", path: "/accessorials", component: AccessorialModule }),
                React.createElement(Route, { exact: true, name: "invoice", path: "/invoices/:id", component: InvoiceDetails }),
                React.createElement(Route, { exact: true, name: "payouts", path: "/payouts", component: PayoutModule }),
                React.createElement(Route, { exact: true, name: "carrier-payouts", path: "/carrier-payouts", component: CarrierPayoutModule }),
                React.createElement(Route, { exact: true, name: "payout", path: "/payouts/:id", component: PayoutDetails }),
                React.createElement(Route, { exact: true, name: "contracts", path: "/contracts", component: ContractList }),
                React.createElement(Route, { exact: true, name: "addcontract", path: "/addcontract/:id", component: ContractsEditorModal }),
                React.createElement(Route, { exact: true, name: "reporting", path: "/reporting", component: GrowthReport }),
                React.createElement(Route, { exact: true, name: "reporting/growth", path: "/reporting/growth", component: GrowthReport }),
                React.createElement(Route, { exact: true, name: "reporting/daily", path: "/reporting/daily", component: DailyReport }),
                React.createElement(Route, { exact: true, name: "tariffs", path: "/tools/tariffs", component: TariffsTable }),
                React.createElement(Route, { exact: true, name: "settings", path: "/settings", component: SettingsModule }),
                React.createElement(AuthorizationWrapper, { excludedUserTypes: [USER_TYPES.CARRIER, USER_TYPES.SHIPPER] },
                    React.createElement(Route, { exact: true, name: "users", path: "/users", component: SettingsModule }),
                    React.createElement(Route, { exact: true, name: "user", path: "/users/:id", component: CustomerDetails }),
                    React.createElement(Route, { exact: true, name: "warehouse", path: "/manage/warehouse", component: ManageWarehouse }),
                    React.createElement(Route, { exact: true, name: "quick_pay", path: "/pricing/quick-pay", component: ManageQuickPayDiscountTerms }),
                    React.createElement(Route, { exact: true, name: "yard-settings", path: "/users/:id/settings/yard", component: YardManagement }),
                    React.createElement(Route, { exact: true, name: "notification-settings", path: "/users/:id/settings/notifications", component: UserNotificationsPage }),
                    React.createElement(Route, { exact: true, name: "team", path: "/users/:id/settings/team", component: Team }),
                    React.createElement(Route, { exact: true, name: "billing", path: "/users/:id/settings/billing", component: BillingV2 }),
                    React.createElement(Route, { exact: true, name: "accessorials", path: "/users/:id/settings/accessorial-requests", component: getAccessorialRequestsPage }),
                    React.createElement(Route, { exact: true, name: "stop-document-requirements-settings", path: "/users/:id/settings/stop-document-requirements", component: getStopDocumentRequirementsPage }),
                    React.createElement(Route, { exact: true, name: "reference-numbers-settings", path: "/users/:id/settings/reference-numbers", component: getReferenceNumberPage }),
                    React.createElement(Route, { exact: true, name: "integration-settings", path: "/users/:id/settings/integrations", component: IntegrationSettings }),
                    React.createElement(Route, { exact: true, name: "account-activity", path: "/users/:id/activity", component: AccountActivity }),
                    React.createElement(Route, { exact: true, name: "carrier-integration-settings", path: "/users/:id/carrier-settings/billing", component: BillingSettings }),
                    React.createElement(Route, { exact: true, name: "carrier-integration-settings", path: "/users/:id/carrier-settings/quick-pay", component: QuickPay }),
                    React.createElement(Route, { exact: true, name: "carrier-integration-settings", path: "/users/:id/carrier-settings/integrations", component: CarrierIntegrationSettings }),
                    React.createElement(Route, { exact: true, name: "carrier-drivers-settings", path: "/users/:id/carrier-settings/drivers", component: DriversSettings }),
                    React.createElement(Route, { exact: true, name: "carrier-notifications-settings", path: "/users/:id/carrier-settings/notifications", component: NotificationsSettings }),
                    React.createElement(Route, { exact: true, name: "carrier-activity-settings", path: "/users/:id/carrier-settings/activity", component: ActivitySettings }),
                    React.createElement(Route, { exact: true, name: "carrier-team-settings", path: "/users/:id/carrier-settings/team", component: CarrierTeam })),
                React.createElement(AuthorizationWrapper, { requiredRoles: [ROLES.ADMIN] },
                    React.createElement(Route, { exact: true, name: "rates", path: "/users/:id/settings/rates", component: DrayageFscSettings }),
                    !isProd() && SessionStore.isAbleToEditShorthaulFSC() && (React.createElement(Route, { exact: true, name: "shorthaulFscSettings", path: "/users/:id/settings/rates/shorthaul/fsc", component: ShorthaulFscSettings })),
                    React.createElement(Route, { exact: true, name: "drayageRateCard", path: "/users/:id/rate-card", component: RateCard })),
                React.createElement(Route, { exact: true, name: "user", path: "/consignee/:id", component: CustomerDetails }),
                React.createElement(Route, { exact: true, name: "profile", path: "/profile", component: CustomerDetails }),
                React.createElement(Route, { exact: true, name: "support-request", path: "/profile/support-request/:id?", component: SupportRequestVerification }),
                React.createElement(Route, { exact: true, name: "carrier-center", path: "/carrier-center", component: CustomerDetails }),
                React.createElement(Route, { exact: true, name: "lane-rate-response", path: "/lane-rate-response/:action", component: LaneRateResponse }),
                React.createElement(Route, { key: "appointments", exact: true, name: "appointments-module", path: "/appointments", component: AppointmentModule }),
                React.createElement(Switch, { key: "appointment-wizard" },
                    React.createElement(Route, { exact: true, name: "new-appointment", path: "/appointments/new", component: AppointmentDetails }),
                    React.createElement(Route, { exact: true, name: "appointment-details", path: "/appointments/:id", component: AppointmentDetails })),
                React.createElement(Route, { exact: true, name: "accessorial-rate-response", path: "/accessorial-rate-response/:action", component: AccessorialRateResponse }),
                React.createElement(Route, { exact: true, name: "carrier-center-account-requests", path: "/carrier-center/account-requests/:id?", component: AccountAccessVerification }),
                React.createElement(Route, { exact: true, name: "profile", path: "/profile/support-request/:id?", component: SupportRequestVerification }),
                React.createElement(Route, { exact: true, name: "carrier-center-support-requests", path: "/carrier-center/support-request/:id?", component: SupportRequestVerification }),
                React.createElement(Route, { exact: true, name: "developer", path: "/developer", component: DeveloperDetails }),
                React.createElement(Route, { exact: true, name: "documents", path: "/documents", component: CarrierDocuments }),
                React.createElement(Route, { exact: true, name: "dashboard", path: "/carriers/dashboard", component: getCarrierDashboardComponent() }),
                React.createElement(Route, { exact: true, name: "bulk-audit-tool", path: "/carriers/bulk-audit-tool", component: BulkAuditTool }),
                React.createElement(Route, { exact: true, name: "billing", path: "/billing", component: getBillingComponent() }),
                React.createElement(Route, { exact: true, name: "notifications", path: "/notifications", component: NotificationsPage }),
                React.createElement(Route, { exact: true, name: "carrier-payout", path: "/carrier-payouts/:id", component: CarrierPayoutDetails }),
                React.createElement(Route, { exact: true, name: "driver-tracking", path: "/driver-tracking", component: DriverMap }),
                React.createElement(Route, { exact: true, name: "driver-list", path: "/drivers", component: DriverList }),
                React.createElement(Route, { exact: true, name: "manage-driver", path: "/drivers/:id/manage", component: DriverEditForm }),
                React.createElement(Route, { exact: true, name: "locations", path: "/locations", component: LocationIndex }),
                React.createElement(Route, { exact: true, name: "location-details", path: "/locations/:id", component: LocationModule }),
                React.createElement(Route, { exact: true, name: "markets", path: "/markets", component: MarketsIndex }),
                React.createElement(Route, { exact: true, name: "orders", path: "/orders", component: OrderList }),
                React.createElement(Route, { exact: true, name: "create-order", path: "/order-booking", component: OrderList }),
                React.createElement(Route, { exact: true, name: "order-details", path: "/orders/:id", component: OrderDetails }),
                React.createElement(Route, { exact: true, name: "order-details-delivery-order", path: "/orders/:id/delivery-order", render: (props) => React.createElement(OrderDetails, Object.assign({}, props)) }),
                React.createElement(Route, { exact: true, name: "customer-list", path: "/customers", component: CustomerList }),
                React.createElement(Route, { exact: true, name: "equipment", path: "/equipment", component: Equipment }),
                React.createElement(PageProvider, null,
                    React.createElement(Route, { exact: true, name: "explore-unifiedview", path: "/unifiedview/favorites", component: Reports }),
                    React.createElement(Route, { exact: true, name: "unifiedview-reports", path: "/unifiedview/my-reports", component: Reports }),
                    React.createElement(Route, { exact: true, name: "unifiedview-reports", path: "/unifiedview/reports", component: Reports }),
                    React.createElement(Route, { exact: true, name: "customer-unifiedview", path: "/unifiedview/dashboard", component: UnifiedViewDashboard }),
                    React.createElement(Route, { exact: true, name: "unifiedview-reports", path: "/unifiedview/groups/:groupId/reports", component: Reports }),
                    React.createElement(Route, { exact: true, name: "report-details", path: "/unifiedview/reports/:reportId", component: ExploreUnifiedView }),
                    React.createElement(Route, { exact: true, name: "explore-unifiedview", path: "/unifiedview/explore", component: ExploreUnifiedView }),
                    React.createElement(Route, { exact: true, name: "explore-order-unifiedview", path: "/unifiedview/orders/explore", component: ExploreOrdersUnifiedView }),
                    React.createElement(Route, { exact: true, name: "column-config", path: "/unifiedview/uv-column-config", component: UVColumnConfig }),
                    React.createElement(Route, { exact: true, name: "unifiedview", path: "/unifiedview", component: ExploreUnifiedView })),
                React.createElement(Route, { exact: true, name: "bulk-shipment-creation", path: "/bulk", component: !isProd() ? BulkShipmentCreation : () => { } }),
                React.createElement(Route, { exact: true, name: "accessorial-management-center", path: "/tools/accessorials", component: AccessorialManagementCenter }),
                React.createElement(Route, { exact: true, name: "zones", path: "/tools/zones", component: Zones }),
                React.createElement(AuthorizationWrapper, null, SessionStore.userAllowedToManageReferenceNumbersMasterList() && (React.createElement(Route, { exact: true, name: "reference-numbers", path: "/tools/reference-numbers", component: ReferenceNumbersMasterList }))),
                React.createElement(Route, { exact: true, name: "carrier-metrics", path: "/carrier-metrics/:id?", component: MetricsPage }),
                React.createElement(Route, { exact: true, name: "markupconfig", path: "/marketplace-config/markup", component: MarketplaceConfig }),
                React.createElement(Route, { exact: true, name: "reasoncodes", path: "/reason_codes", component: ReasonCodes }),
                React.createElement(Route, { exact: true, name: "loadboard", path: "/loadboard", component: LoadBoard }),
                React.createElement(Route, { exact: true, name: "accessorial-rates-rate-conditions", path: "/accessorial-rates-v2", component: AccessorialRatesRateConditions }),
                React.createElement(Route, { exact: true, name: "lane-rates-rate-conditions", path: "/lane-rates", component: LaneRatesRC }),
                React.createElement(Route, { exact: true, name: "lane-rates-add", path: "/lane-rates/add", render: (props) => getLaneRatesRateConditionsEdit(props) }),
                React.createElement(Route, { exact: true, name: "lane-rates-edit", path: "/lane-rates/edit", render: (props) => getLaneRatesRateConditionsEdit(props) }),
                React.createElement(Route, { exact: true, name: "accessorial-rates", path: "/accessorial-rates", render: (props) => getAccessorialRates(props) }),
                React.createElement(Route, { exact: true, name: "offer-planner", path: "/offer-planner", component: (props) => getOfferPlanner(props) }),
                React.createElement(Route, { exact: true, name: "tools", path: "/tools", component: ToolsPage }),
                React.createElement(AuthorizationWrapper, { requiredRoles: [ROLES.ADMIN] },
                    React.createElement(Route, { exact: true, name: "yard-details", path: "/yards/:id", component: YardDetails }),
                    React.createElement(Route, { exact: true, name: "market-rates", path: "/yards/:id/rates", component: MarketRates }),
                    React.createElement(Route, { exact: true, name: "yards", path: "/yards", component: YardsList })))))));
}
function authorize(router) {
    let query;
    const currentPath = router.location.pathname;
    const isLoggedIn = SessionStore.isLoggedIn();
    const excludePaths = ['/tracking', '/empty-returns/instructions'];
    query = getQueryString(router.location.search);
    if (!isLoggedIn) {
        if (excludePaths.includes(currentPath)) {
            SessionStore.setRedirectPath(currentPath);
            const redir = `${currentPath}${router.location.search}`;
            return React.createElement(Redirect, { to: redir });
        }
        if (currentPath !== '/login') {
            if (query.action) {
                SessionStore.setRedirectPath(`${currentPath}/${router.location.search}`);
            }
            else {
                SessionStore.setRedirectPath(currentPath);
            }
        }
        if (currentPath === '/passwordreset' || currentPath === '/createpassword') {
            SessionStore.setRedirectPath('/');
            const redir = `${currentPath}${router.location.search}`;
            return React.createElement(Redirect, { to: redir });
        }
        if (currentPath === '/profile/support-request/' ||
            currentPath === '/carrier-center/support-request/' ||
            currentPath.startsWith('/lane-rate-response/') ||
            currentPath.startsWith('/accessorial-rate-response/')) {
            const redir = `${currentPath}${router.location.search}`;
            SessionStore.setRedirectPath(redir);
            return React.createElement(Redirect, { to: "/login" });
        }
        return React.createElement(Redirect, { to: "/login" });
    }
    const { _type: type, authorized } = SessionStore.getUser();
    let redirect = SessionStore.getRedirectPath();
    // redirect to welcome page if carrier is not authorized
    const newCarrierPages = [
        '/dashboard',
        '/documents',
        '/carrier-center',
        '/notifications',
        '/billing',
    ];
    if (redirect) {
        query = getQueryString(redirect);
    }
    // Link support requests
    if (type && (type === ROLES.SHIPPER || type === ROLES.CARRIER)) {
        if (redirect && redirect.includes('/support-request/')) {
            return renderPrivate(redirect, currentPath);
        }
        if (redirect && redirect.includes('/lane-rate-response/')) {
            return renderPrivate(redirect, currentPath);
        }
        if (redirect && redirect.includes('/accessorial-rate-response/')) {
            return renderPrivate(redirect, currentPath);
        }
    }
    if (type === ROLES.CARRIER &&
        !authorized &&
        !newCarrierPages.includes(currentPath)) {
        //  redirect to my documents if user is not authorized
        redirect = '/documents';
    }
    else if (type === ROLES.CARRIER &&
        authorized &&
        currentPath === '/login' &&
        !checkRedirectPath(query, redirect)) {
        redirect = '/driver-tracking';
    }
    else if (type === ROLES.SHIPPER && currentPath === '/login') {
        redirect = '/';
    }
    else if (!redirect && currentPath === '/login') {
        redirect = '/';
    }
    else if (type === ROLES.SHIPPER && SessionStore.isQuotesOnly()) {
        redirect = '/drayage-quotes';
    }
    else if (type && type === USER_TYPES.WAREHOUSE) {
        return renderWarehouseRoutes(redirect, currentPath);
    }
    return renderPrivate(redirect, currentPath);
}
function Router(props) {
    var _a;
    const data = useContext(AuthContext);
    const user = SessionStore.getUser();
    const roles = SessionStore.getUserRoles();
    return (React.createElement(AuthContext.Provider, { value: Object.assign(Object.assign({}, data), { roles, _type: ((_a = user === null || user === void 0 ? void 0 : user._type) === null || _a === void 0 ? void 0 : _a.toLowerCase()) || null }) },
        React.createElement(BrowserRouter, { onError: routerErrorHandler },
            React.createElement(ErrorBoundary, null,
                React.createElement("div", { className: classnames({ 'pt-16': props.nav }) },
                    props.nav && [
                        React.createElement(props.nav, { key: "nav" }),
                        React.createElement(CacheBusting, { key: "cache-busting" }),
                    ],
                    React.createElement(Route, { name: "login", path: "/login", component: Login }),
                    React.createElement(Route, { name: "passwordreset", path: "/passwordreset", component: PasswordReset }),
                    React.createElement(Route, { name: "createpassword", path: "/createpassword", component: PasswordReset }),
                    React.createElement(Route, { name: "tracking", path: "/tracking", component: PublicShipmentTracking }),
                    React.createElement(Route, { name: "empty-returns-instructions", path: "/empty-returns/instructions", component: EmptyReturnInstructions }),
                    React.createElement(Route, { path: "*", render: authorize }))))));
}
Router.propTypes = {
    nav: PropTypes.func.isRequired,
};
export default Router;
